// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { User } from '@/account/api/get-organization-users';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { ForwardedRef, HTMLAttributes, forwardRef } from 'react';

/**
 * Determines if the option is equal to the value.
 *
 * @param option - A person option to compare.
 * @param value - A person value to compare.
 */
function isOptionEqualToValue(option: User, value: User) {
  if (!value) {
    return false;
  }
  return option.id === value.id;
}

/**
 * Renders a single person option.
 *
 * @param props - The HTML attributes to apply to the list item.
 * @param option - A person option to render.
 */
function renderOption(props: HTMLAttributes<HTMLLIElement>, option: User) {
  const displayName = `${option.firstName} ${option.lastName}`;
  const emailDisplay = option.email ? ` (${option.email})` : '';

  return (
    <li {...props} key={option.id}>
      <Typography color={'text.primary'}>
        {displayName}
        {emailDisplay}
      </Typography>
    </li>
  );
}

/**
 * Returns the label of the option.
 *
 * @param option - A person option to get the label from.
 */
function getOptionLabel(option: User) {
  return option.firstName + ' ' + option.lastName;
}

interface Props<Option extends User>
  extends Omit<
    AutocompleteProps<Option, false, false, false>,
    | 'renderInput'
    | 'isOptionEqualToValue'
    | 'getOptionLabel'
    | 'groupBy'
    | 'renderOption'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;
}

/**
 * Renders a person autocomplete component and forwards a ref to the input
 * element.
 */
function PersonAutocompleteRenderer(
  props: Props<User>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const {
    name,
    label,
    placeholder,
    error,
    helperText,
    value,
    ...autocompleteProps
  } = props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  return (
    <Autocomplete<User, false, false, false>
      {...autocompleteProps}
      value={value ?? null} // Ensure value is never undefined
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={createFilterOptions({
        ignoreCase: true,
        matchFrom: 'any',
        stringify: (option: User) =>
          option.firstName + ' ' + option.lastName + ' ' + (option.email || ''),
      })}
    />
  );
}

/**
 * An autocomplete component for selecting a person from a list of options.
 */
export const PersonAutocomplete = forwardRef(PersonAutocompleteRenderer);
